import type { RuleContext, Values } from "./types.js";

/**
 * Named checks for logic a pattern can't express. A rule refers to one by name
 * (`{ kind: "check", check: "in.pan.holderType" }`), so specs stay plain data.
 * A check returns a message when it fails, or null.
 */
export type CheckFn = (
  value: string,
  values: Values,
  ctx: RuleContext,
  params: Record<string, string | number | boolean>,
) => string | null;

function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? (parts[parts.length - 1] ?? "") : "";
}

export const CHECKS: Record<string, CheckFn> = {
  /** A name on a tax form should have at least two parts. */
  "name.twoParts": (v) => (/\S\s+\S/.test(v) ? null : "Use your full name as on your ID — first and last name."),

  /** India PAN: the 4th character is the holder type; P is a person. */
  "in.pan.holderType": (v) => {
    const p = v.toUpperCase();
    if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(p)) return null; // the pattern rule reports the format
    return p[3] === "P" ? null : "The 4th letter of a personal PAN is P. Check it matches your PAN card.";
  },

  /** India PAN: for a person, the 5th character is usually the first letter of the surname. */
  "in.pan.surnameInitial": (v, values, _ctx, params) => {
    const p = v.toUpperCase();
    if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(p) || p[3] !== "P") return null;
    const nameField = String(params.nameField ?? "name");
    const s = surname(String(values[nameField] ?? ""));
    if (!s) return null;
    const initial = s[0]!.toUpperCase();
    if (!/[A-Z]/.test(initial)) return null;
    return p[4] === initial
      ? null
      : `The 5th letter of a PAN is usually the first letter of your surname (${initial}). Check it matches your PAN card.`;
  },

  /** Two fields should normally hold the same value (e.g. treaty country = country of residence). */
  "sameAs": (v, values, _ctx, params) => {
    const other = String(values[String(params.field)] ?? "").trim();
    if (!other || other === v.trim()) return null;
    return String(params.message ?? "This usually matches your earlier answer.");
  },

  /** Flag: the name differs from the one the sender has on file. */
  "sender.nameMatches": (v, _values, ctx) => {
    const onFile = String(ctx.sender?.recordName ?? "").trim();
    if (!onFile) return null;
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");
    return norm(onFile) === norm(v) ? null : `Name differs from the one on file (“${onFile}”).`;
  },

  /** Flag ("reason to know"): the sender pays this person into a U.S. bank account. */
  "sender.usBankAccount": (_v, _values, ctx) =>
    ctx.sender?.payToCountry === "US" ? "Payments go to a U.S. bank account — check this person's foreign status before relying on the form." : null,

  /** Flag ("reason to know"): the sender's record has a U.S. phone number and no foreign one. */
  "sender.usPhoneOnly": (_v, _values, ctx) =>
    ctx.sender?.phoneCountry === "US" ? "The record has only a U.S. phone number — check this person's foreign status." : null,
};

export function runCheck(
  name: string,
  value: string,
  values: Values,
  ctx: RuleContext,
  params: Record<string, string | number | boolean> = {},
): string | null {
  const fn = CHECKS[name];
  if (!fn) throw new Error(`Unknown check "${name}"`);
  return fn(value, values, ctx, params);
}
